import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { TextInput, DateInputV4, NumbersInputWithSymbol } from 'wg-fe-ui';
import { Change } from '../../../../../../../types/Form';
import {
  CarInputs,
  ReturnOptionProps,
} from '../../../../../../../types/ProductFactory';
import {
  chakraCustomScrollBar,
  mq,
} from '../../../../../../common/src/utils/styledComponentsUtils';
import carFuelTypes from '../../../../../../sales-conversation/src/constants/carFuelTypes.json';
import SearchSelectInputV2 from '../../../../../../common/src/components/SearchSelectInputV2';
import {
  Box,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalFooter,
  ModalBody,
  Button,
  Center,
  ModalCloseButton,
  HStack,
  Tooltip,
  Icon,
} from '@wegroup/design-system';
import QuestionGroupAssistantQuestion from '../../../ui/advisory-flow/components/question-group-components/QuestionGroupAssistantQuestion';
import useFlowsCurrency from '../../../../hooks/useFlowsCurrency';

interface Values {
  [k: string]: unknown;
}

interface Props {
  setIsModalOpen: (arg0: boolean) => void;
  values: Values;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors: any;
  setFieldValue: (
    arg0: string,
    arg1: unknown,
    options?: Record<string, boolean>,
  ) => void;
  showModal: boolean;
  carInputs: CarInputs[];
  handleModalYes: () => void;
  handleNewSearch: () => void;
}

const VehicleDataModal: React.FC<React.PropsWithChildren<Props>> = ({
  setIsModalOpen,
  showModal,
  setFieldValue,
  carInputs,
  handleModalYes,
  handleNewSearch,
  values,
  errors,
}) => {
  const { t } = useTranslation();
  const { symbol } = useFlowsCurrency();

  return (
    <Modal
      onClose={() => setIsModalOpen(false)}
      isOpen={showModal}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent maxW="70rem" w="100%" m="2" overflow="hidden">
        <ModalCloseButton />
        <ModalBody p="8" overflowX="hidden" sx={chakraCustomScrollBar}>
          <QuestionGroupAssistantQuestion
            question={t('Please adjust the car data')}
            description={t(
              'Click save at the bottom of this screen to save all your adjusted car data',
            )}
            mb="4"
            noPadding
          />

          <VehicleDataContainer data-test-id="ProdFac_VehicleData_DataContainer">
            {carInputs.map(({ key, name, value, type, required, tooltip }) => {
              const displayName = required ? name + ' *' : name;
              return (
                {
                  DATE: (
                    <StyledDateInputV4
                      key={key}
                      name={displayName}
                      initialValue={values[key] || value}
                      error={errors[key]?.message}
                      onChange={(e: Change) => {
                        if (!e.value) return;
                        setFieldValue(key, (e.value as string).split('T')[0], {
                          shouldValidate: true,
                        });
                      }}
                      dataTestId={key}
                      iso
                    >
                      {displayName}
                    </StyledDateInputV4>
                  ),
                  CURRENCY_INCL_VAT: (
                    <Box>
                      <Text
                        color="#8990A3"
                        fontSize="sm"
                        mb="2"
                        lineHeight="120%"
                      >
                        {`${displayName} (${t('Incl VAT')})`}
                      </Text>
                      <StyledNumbersInputWithSymbol
                        name={displayName}
                        type="number"
                        step=".01"
                        placeholder="0.00"
                        error={errors?.[key]?.message}
                        value={values[key] || value}
                        initalValue={Number(values[key] || value).toFixed(2)}
                        onChange={(e: Change) => {
                          if (!e.value) return setFieldValue(key, null);
                          setFieldValue(key, e.value, {
                            shouldValidate: true,
                          });
                        }}
                        data-test-id="ProdFac_CurrencyInputComponent"
                        icon={symbol}
                      />
                    </Box>
                  ),
                  CURRENCY: (
                    <Box>
                      <Text
                        color="#8990A3"
                        fontSize="sm"
                        mb="2"
                        lineHeight="120%"
                      >
                        {`${displayName} (${t('Excl VAT')})`}
                      </Text>
                      <StyledNumbersInputWithSymbol
                        name={displayName}
                        type="number"
                        step=".01"
                        placeholder="0.00"
                        error={errors?.[key]?.message}
                        value={values[key] || value}
                        initalValue={values[key] || value}
                        onChange={(e: Change) => {
                          if (!e.value) return setFieldValue(key, null);
                          setFieldValue(key, e.value, {
                            shouldValidate: true,
                          });
                        }}
                        data-test-id="ProdFac_CurrencyInputComponent"
                        icon={symbol}
                      />
                    </Box>
                  ),
                  FUEL_SELECT: (
                    <SearchSelectInputV2
                      name={displayName}
                      onSelected={(selectedItem: ReturnOptionProps) =>
                        setFieldValue(key, selectedItem.value, {
                          shouldValidate: true,
                        })
                      }
                      options={Object.entries(carFuelTypes).map(
                        ([key, value]) => ({
                          label: t(value),
                          value: key,
                        }),
                      )}
                      error={errors?.[key]?.message}
                      value={{
                        label: t(
                          carFuelTypes[(values[key] || value) as string],
                        ),
                        value: values[key] || value,
                      }}
                      placeholder={t('Choose an option')}
                    >
                      {t('Fuel type')}
                    </SearchSelectInputV2>
                  ),
                }[type] || (
                  <StyledTextInput
                    key={key}
                    name={displayName}
                    value={values[key] || value}
                    error={errors?.[key]?.message}
                    onChange={(e: Change) =>
                      setFieldValue(key, e.value ? e.value : null)
                    }
                  >
                    <HStack>
                      <Text>{displayName}</Text>
                      {tooltip && (
                        <Tooltip label={tooltip} shouldWrapChildren>
                          <Icon name="GeneralStatusInfo" />
                        </Tooltip>
                      )}
                    </HStack>
                  </StyledTextInput>
                )
              );
            })}
          </VehicleDataContainer>
          <Center mt="6">
            <Button
              borderRadius="full"
              onClick={handleNewSearch}
              variant="secondary"
            >
              {t('FLOWS_INPUT_CAR-INPUT_NEW-SEARCH')}
            </Button>
          </Center>
        </ModalBody>
        <ModalFooter
          as={HStack}
          spacing="3"
          data-test-id="ADVISORY-CONVERSATION_QUESTIONS_CAR-DATA-MODAL-FOOTER"
        >
          <Button variant="ghost" onClick={() => setIsModalOpen(false)}>
            {t('CANCEL')}
          </Button>
          <Button onClick={handleModalYes}>{t('SAVE')}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const StyledNumbersInputWithSymbol = styled(NumbersInputWithSymbol)`
  height: unset;

  span {
    top: 1px;
    height: 100%;
    padding: 0 1.2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-color: #e4e4e4;
  }

  > label > input {
    margin-top: 0;
    padding-left: 1.2rem;

    ${({ prefilled }) =>
      prefilled ? `border-color: #FFBC0F; background-color: #FFF9EA;` : ''}
  }

  > div {
    /* Errorcontainer */
    margin: 0;
    font-size: 1.1rem;
  }
`;

const StyledDateInputV4 = styled(DateInputV4)`
  height: unset;

  > div {
    /* Errorcontainer */
    height: unset;
    margin-bottom: 0;
  }
`;

const StyledTextInput = styled(TextInput)`
  height: auto;

  > div {
    /* Errorcontainer */
    margin: 0;
    font-size: 1.1rem;
  }
`;

const VehicleDataContainer = styled.div`
  display: grid;
  gap: 1.6rem;

  ${mq.tablet} {
    grid-template-columns: repeat(2, 1fr);
  }
`;

export default VehicleDataModal;
